import { useState, useCallback, useEffect, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import { useCtaStore, useRoomStore } from '@src/stores';

import { ICTAMedia } from '@src/types/cta.types';

interface IStepperState {
  minutes: number | '';
  seconds: number | '';
}

export const useCreateCta = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // Duration State (Min 5, Max 15)
  const [duration, setDuration] = useState<IStepperState>({ minutes: '', seconds: '' });

  // Starting In State
  const [startingIn, setStartingIn] = useState<IStepperState>({ minutes: '', seconds: '' });

  const [isLiveNow, setIsLiveNow] = useState(false);

  const [buttonText, setButtonText] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [media, setMedia] = useState<ICTAMedia[] | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const [warning, setWarning] = useState<string | null>(null);

  // Get room context and store values
  const { isHost } = useRoomContext();

  const isJoinedRoom = useRoomStore(useShallow((state) => state.isJoinedRoom));
  const selectedCta = useCtaStore(useShallow((state) => state.selectedCta));

  const { isCreateCtaModalOpen, setIsCreateCtaModalOpen } = useCtaStore(
    useShallow((state) => ({
      isCreateCtaModalOpen: state.isCreateCtaModalOpen,
      setCtas: state.setCtas,
      setIsCreateCtaModalOpen: state.setIsCreateCtaModalOpen,
    }))
  );

  // Determine if the live action section should be shown
  const shouldShowLiveNow = useMemo(() => isHost && isJoinedRoom, [isHost, isJoinedRoom]);

  useEffect(() => {
    if (selectedCta) {
      setTitle(selectedCta.title);
      setDescription(selectedCta.description || '');

      const durM = Math.floor(selectedCta.ctaDuration / 60);
      const durS = selectedCta.ctaDuration % 60;
      setDuration({ minutes: durM, seconds: durS });

      const startM = Math.floor(selectedCta.ctaDuration / 60);
      const startS = selectedCta.ctaDuration % 60;
      setStartingIn({ minutes: startM, seconds: startS });

      setIsLiveNow(selectedCta.status === 'LIVE');
      setButtonText(selectedCta.buttonText || '');
      setButtonUrl(selectedCta.buttonUrl || '');
      // Media file handling in edit mode might need URL handling if already uploaded,
      // but for now keeping it simple as File | null.
      // Ideally we would have a separate 'mediaUrl' state for existing media.
      setMedia(selectedCta.media || null);
    } else {
      // Reset if needed or rely on resetForm
    }
  }, [selectedCta]);

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setDuration({ minutes: '', seconds: '' });
    setStartingIn({ minutes: '', seconds: '' });
    setIsLiveNow(false);
    setButtonText('');
    setButtonUrl('');
    setMedia(null);
    setUploadedFiles([]);
    setWarning(null);
  }, []);

  // --- Duration Logic ---

  const handleDurationFocus = useCallback(() => {
    setDuration((prev) => {
      if (prev.minutes === '' && prev.seconds === '') {
        return { minutes: 5, seconds: 0 };
      }
      return prev;
    });
  }, []);

  const incDurationMin = useCallback(() => {
    setDuration((prev) => {
      let m = prev.minutes === '' ? 5 : prev.minutes;
      if (m >= 15) {
        setWarning('The CTA duration must be between 5 and 15 minutes.');
        return { minutes: 15, seconds: 0 };
      }

      return { minutes: m + 1, seconds: 0 };
    });
    setWarning(null);
  }, []);

  const decDurationMin = useCallback(() => {
    setDuration((prev) => {
      let m = prev.minutes === '' ? 5 : prev.minutes;
      let s = prev.seconds === '' ? 0 : prev.seconds;

      if (m <= 5) {
        setWarning('The CTA duration must be between 5 and 15 minutes.');
        return { minutes: 5, seconds: s };
      }

      return { minutes: m - 1, seconds: s };
    });
    setWarning(null);
  }, []);

  const incDurationSec = useCallback(() => {
    setDuration((prev) => {
      let m = prev.minutes === '' ? 5 : prev.minutes;
      let s = prev.seconds === '' ? 0 : prev.seconds;

      if (m >= 15) {
        setWarning('The CTA duration must be between 5 and 15 minutes.');
        return { minutes: 15, seconds: 0 };
      }

      if (s >= 59) {
        const newM = m + 1;
        if (newM > 15) {
          return { minutes: 15, seconds: 0 };
        }
        return { minutes: newM, seconds: 0 };
      }

      return { minutes: m, seconds: s + 1 };
    });
    setWarning(null);
  }, []);

  const decDurationSec = useCallback(() => {
    setDuration((prev) => {
      let m = prev.minutes === '' ? 5 : prev.minutes;
      let s = prev.seconds === '' ? 0 : prev.seconds;

      if (s <= 0) {
        if (m <= 5) {
          setWarning('The CTA duration must be between 5 and 15 minutes.');
          return { minutes: 5, seconds: 0 };
        }
        return { minutes: m - 1, seconds: 59 };
      }

      return { minutes: m, seconds: s - 1 };
    });
    setWarning(null);
  }, []);

  // --- Starting In Logic ---
  const incStartingMin = useCallback(() => {
    if (isLiveNow) return;
    setStartingIn((prev) => {
      const m = prev.minutes === '' ? 0 : prev.minutes;
      const s = prev.seconds === '' ? 0 : prev.seconds;
      return { minutes: m + 1, seconds: s };
    });
  }, [isLiveNow]);

  const decStartingMin = useCallback(() => {
    if (isLiveNow) return;
    setStartingIn((prev) => {
      const m = prev.minutes === '' ? 0 : prev.minutes;
      const s = prev.seconds === '' ? 0 : prev.seconds;
      return { minutes: Math.max(0, m - 1), seconds: s };
    });
  }, [isLiveNow]);

  const incStartingSec = useCallback(() => {
    if (isLiveNow) return;
    setStartingIn((prev) => {
      const m = prev.minutes === '' ? 0 : prev.minutes;
      const s = prev.seconds === '' ? 0 : prev.seconds;
      if (s >= 59) {
        return { minutes: m + 1, seconds: 0 };
      }
      return { minutes: m, seconds: s + 1 };
    });
  }, [isLiveNow]);

  const decStartingSec = useCallback(() => {
    if (isLiveNow) return;
    setStartingIn((prev) => {
      const m = prev.minutes === '' ? 0 : prev.minutes;
      const s = prev.seconds === '' ? 0 : prev.seconds;
      if (s <= 0) {
        if (m > 0) return { minutes: m - 1, seconds: 59 };
        return { minutes: 0, seconds: 0 };
      }
      return { minutes: m, seconds: s - 1 };
    });
  }, [isLiveNow]);

  const toggleLiveNow = useCallback(() => {
    setIsLiveNow((prev) => {
      const newVal = !prev;
      if (newVal) {
        setStartingIn({ minutes: 0, seconds: 0 });
      }
      return newVal;
    });
  }, []);

  const closeModal = useCallback(() => {
    resetForm();
    setIsCreateCtaModalOpen(false);
  }, [resetForm, setIsCreateCtaModalOpen]);

  const submitForm = useCallback(() => {
    // console.log('Create/Edit CTA integration would happen here', { ...formState });
    // In future: addCta(...) or updateCta(...)
    closeModal();
  }, [closeModal]);

  const isValid =
    title.trim().length > 0 &&
    duration.minutes !== '' &&
    duration.minutes >= 5 &&
    duration.minutes <= 15 &&
    (!buttonUrl || /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i.test(buttonUrl)) &&
    ((buttonText.trim().length > 0 && buttonUrl.trim().length > 0) || !!media || uploadedFiles.length > 0);

  const [urlError, setUrlError] = useState<string | null>(null);

  const handleUrlBlurState = useCallback(() => {
    if (!buttonUrl) {
      setUrlError(null);
      return;
    }

    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
    if (!urlPattern.test(buttonUrl)) {
      setUrlError('Please enter a valid URL');
    } else {
      setUrlError(null);
    }
  }, [buttonUrl]);

  const handleSetButtonUrl = useCallback(
    (val: string) => {
      setButtonUrl(val);
      if (urlError) setUrlError(null);
    },
    [urlError]
  );

  return {
    formState: {
      title,
      description,
      duration,
      startingIn,
      isLiveNow,
      buttonText,
      buttonUrl,
      urlError,
      media,
      uploadedFiles,
      warning,
      isEditMode: !!selectedCta,
      shouldShowLiveNow,
    },
    actions: {
      setTitle,
      setDescription,
      handleDurationFocus,
      incDurationMin,
      decDurationMin,
      incDurationSec,
      decDurationSec,
      setStartingIn,
      incStartingMin,
      decStartingMin,
      incStartingSec,
      decStartingSec,
      toggleLiveNow,
      setButtonText,
      setButtonUrl: handleSetButtonUrl,
      handleUrlBlur: handleUrlBlurState,
      setMedia,
      setUploadedFiles,
      clearWarning: () => setWarning(null),
      resetForm,
      closeModal,
      submitForm,
    },
    isValid,
    isModalOpen: isCreateCtaModalOpen,
  };
};
